import { FC, Fragment } from 'react';
import { Button, Col, Divider, Form, Row, Select, Table } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import DataRow from '@/libs/antd/components/DataRow';
import FleetTitle from '@/libs/antd/components/Title';
import {
	getHeadSurveyDetail,
	submitFHApprovalSurvey,
	SubmitFHApprovalSurveyPayload,
} from '@/libs/fleet-api/services/survey';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { useSelectRejectCategory } from '@/libs/fleet-api/hooks/master-data';
import { useForm } from '@/libs/antd/form';
import { useApp } from '@/App';
import rule from '@/libs/antd/rules';
import TextArea from 'antd/es/input/TextArea';
import dayjs from 'dayjs';
import { useModals } from '@/components/modals/ModalProvider';
import { DocumentItem } from '@/components/modals/DocumentModal';
import fetcher from '@/libs/fleet-api/fetcher';
import GenericErrorMessage from '@/components/GenericErrorMessage';

export type FHSurveyApprovalPageProps = {
	prospectId: string;
};

const FHSurveyApprovalPage: FC<FHSurveyApprovalPageProps> = ({
	prospectId,
}) => {
	const { navigate, notification } = useApp();
	const { data, isLoading, isError } = useQuery({
		queryKey: ['getHeadSurveyDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getHeadSurveyDetail(id);
		},
	});

	const {
		openCustomerDetail,
		openCustomerGroupDetail,
		openProspectDetail,
		openDocumentModal,
		openPraSurveyDataModal,
		openPraSurveyAnalysisModal,
		openMapModal,
	} = useModals();

	const {
		mainInformation,
		surveyInformation,
		approvalHistories,
		customerBusinessDocuments,
		customerLegalDocuments,
		customerReferralDocuments,
	} = data?.data ?? {};

	const surveyParticipants = surveyInformation?.surveyParticipants;

	const { form, value, fieldName } = useForm<SubmitFHApprovalSurveyPayload>();

	const selectRejectCategory = useSelectRejectCategory();
	const submitFHApprovalSurveyMutation = useMutation({
		mutationFn: submitFHApprovalSurvey,
	});
	const handleSubmit = async (values: SubmitFHApprovalSurveyPayload) => {
		try {
			await submitFHApprovalSurveyMutation.mutateAsync(values);
			notification.success({
				message: 'Success',
				description: 'Approval has been submitted',
			});
			navigate('FHSurveyApprovalListPage');
		} catch (error) {
			if (fetcher.isError(error)) {
				const message = await error.json();
				notification.error({
					duration: 20,
					message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
					description: (
						<div>
							{message?.metadata?.debugInfo?.messages &&
							message?.metadata?.debugInfo?.messages.length ? (
								<ul>
									{message?.metadata?.debugInfo?.messages.map((msg: string) => (
										<li>{msg}</li>
									))}
								</ul>
							) : (
								<pre>Opps, Something went wrong</pre>
							)}
						</div>
					),
				});
			}
		}
	};

	if (isError) return <GenericErrorMessage />;

	if (isLoading) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Fragment>
			<FleetTitle>Main Information</FleetTitle>
			<DataRow
				loading={isLoading}
				items={[
					[
						[
							'Nama Customer',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openCustomerDetail(mainInformation!.customerId);
								}}
							>
								{mainInformation?.customerName}
							</a>,
						],
						[
							'Prospect ID',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openProspectDetail(mainInformation!.prospectId);
								}}
							>
								{mainInformation?.prospectId}
							</a>,
						],
					],
					[
						['Kelas Industri', mainInformation?.industrySector.industryClass],
						['Sumber Order', mainInformation?.orderSource],
					],
					[
						[
							'Portofolio Guideline',
							mainInformation?.industrySector.portofolioGuideline,
						],
						[
							'Group Customer BMPP',
							mainInformation?.customerGroup?.id ? (
								<a
									href="#"
									className="trigger-popup"
									onClick={(e) => {
										e.preventDefault();
										openCustomerGroupDetail(mainInformation!.customerGroup.id);
									}}
									aria-disabled={!mainInformation?.customerGroup.id}
								>
									{mainInformation?.customerGroup.value ?? '-'}
								</a>
							) : (
								'-'
							),
						],
					],
					[
						[
							'Dokumen',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();

									const documents: DocumentItem[] = [];
									const getDocumentType = (name: string) => {
										if (name.endsWith('pdf')) return 'pdf';
										if (name.endsWith('jpeg')) return 'jpeg';
										if (name.endsWith('jpg')) return 'jpg';
										if (name.endsWith('png')) return 'png';
									};

									if (customerLegalDocuments && customerLegalDocuments.length) {
										customerLegalDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (
										customerBusinessDocuments &&
										customerBusinessDocuments.length
									) {
										customerBusinessDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (
										customerReferralDocuments &&
										customerReferralDocuments.length
									) {
										customerReferralDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (documents.length) {
										openDocumentModal(documents);
									} else {
										alert('no document found');
									}
								}}
							>
								View Dokumen
							</a>,
						],
						[
							'Data Pra-Survey',
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									openPraSurveyDataModal(prospectId);
								}}
								className="trigger-popup"
							>
								Detail Pra-Survey
							</a>,
						],
					],
					[
						[
							'Analisa Pra-Survey',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openPraSurveyAnalysisModal(prospectId);
								}}
							>
								Detail Analisa
							</a>,
						],
					],
				]}
				style={{ marginBottom: 20 }}
			/>

			<FleetTitle>Survey Information</FleetTitle>
			<DataRow
				items={[
					[
						['Department', surveyInformation?.department],
						['Nama FRM', mainInformation?.frm.value],
					],
					[
						[
							'Lokasi Survey Kantor',
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									openMapModal({
										title: 'Lokasi Survey Kantor',
										address: surveyInformation?.officeAddress,
										latitude: surveyInformation?.officeLat,
										longitude: surveyInformation?.officeLong,
										draggable: false,
										viewOnly: true,
									});
								}}
							>
								{surveyInformation?.officeAddress}
							</a>,
						],
						[
							'Tanggal Survey Kantor',
							dayjs(surveyInformation?.surveyOfficeDate).format('DD/MM/YYYY'),
						],
					],
					[
						[
							'Lokasi Survey Site',
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									openMapModal({
										title: 'Lokasi Survey Site',
										address: surveyInformation?.siteAddress,
										latitude: surveyInformation?.siteLat,
										longitude: surveyInformation?.siteLong,
										draggable: false,
										viewOnly: true,
									});
								}}
							>
								{surveyInformation?.siteAddress}
							</a>,
						],
						[
							'Tanggal Survey Site',
							dayjs(surveyInformation?.surveySiteDate).format('DD/MM/YYYY'),
						],
					],
					[
						['Peserta Survey (FRM)', mainInformation?.frm?.value],
						['Peserta Survey (CA)', mainInformation?.creditAnalyst?.value],
					],
				]}
				style={{ marginBottom: 20 }}
			/>
			<Divider />
			<Table
				scroll={{ x: 'max-content' }}
				dataSource={
					surveyParticipants?.map((record, key) => {
						return { ...record, key };
					}) ?? []
				}
				columns={[
					{
						title: 'No',
						render: (_, record) => record.key + 1,
					},
					{
						title: 'Nama Peserta Survey',
						dataIndex: ['name'],
					},
					{
						title: 'Jabatan Peserta Survey',
						dataIndex: ['position'],
					},
				]}
				bordered
				pagination={false}
				style={{ marginBottom: 20 }}
			/>
			<FleetTitle>Approval History</FleetTitle>
			<Table
				dataSource={
					approvalHistories?.map((record, key) => {
						return { ...record, key };
					}) ?? []
				}
				columns={[
					{
						title: 'No',
						width: 1,
						align: 'center',
						render: (_, __, index) => index + 1,
					},
					{
						title: 'Name',
						dataIndex: 'approvalBy',
					},
					{
						title: 'Date',
						dataIndex: 'approvalDate',
					},
					{
						title: 'Process',
						dataIndex: 'approvalProcess',
					},
					{
						title: 'Status',
						align: 'center',
						render: (_, record) => {
							return (
								<span
									className={`review-status ${record.approvalStatus.toLowerCase()}`}
								>
									{record.approvalStatus}
								</span>
							);
						},
					},
					{
						title: 'Approval Notes',
						dataIndex: 'approvalNote',
					},
				]}
				bordered
				pagination={false}
				style={{ marginBottom: 20 }}
			/>

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				onFinish={handleSubmit}
			>
				<Form.Item
					name={fieldName('prospectId')}
					initialValue={mainInformation?.prospectId}
					noStyle
				/>
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('approvalStatus')}
							label="Approval Status"
							rules={[rule.required()]}
						>
							<Select
								allowClear={true}
								options={[
									{ value: 'Approve' },
									// { value: 'Return' },
									{ value: 'Reject' },
								]}
							/>
						</Form.Item>
					</Col>
					{value?.approvalStatus === 'Reject' && (
						<Col span={12}>
							<Form.Item
								name={fieldName('rejectCategoryId')}
								label="Category Reject"
								rules={[rule.required()]}
							>
								<Select {...selectRejectCategory} />
							</Form.Item>
						</Col>
					)}
				</Row>
				<Divider />
				<Form.Item
					name={fieldName('approvalNotes')}
					label="Approval Notes"
					labelCol={{ span: 6 }}
					wrapperCol={{
						style: {
							color: 'blue',
							marginLeft: -10,
						},
					}}
					rules={[rule.required()]}
				>
					<TextArea rows={3} />
				</Form.Item>
				<Button
					loading={submitFHApprovalSurveyMutation.isPending}
					type="primary"
					htmlType="submit"
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
						marginTop: 40,
					}}
				>
					SAVE
				</Button>
			</Form>
		</Fragment>
	);
};

export default FHSurveyApprovalPage;
